/**
 * 「完成时填多少」这件事的**唯一判定入口** —— 界面上不许再各写一套。
 *
 * ## 为什么要抽出来（2026-10 用户原话）
 *
 * > 打卡，孩子打卡的时候，打卡完了点完成嘛。点完成的时候，要输入实际完成量，
 * > 你默认写的是**目标值**，但有时候可能没有完成目标量，或者超量完成了。
 * > 如果用户没留意，直接点「完成」，**实际完成量就不对了**。
 * > 所以，请你**把目标值留空**。**不填的话就跳不过去**，让他自己去填那个数。
 *
 * 原来的两处界面（`StudentTodayPage`、`StudentHomeworkPage`）都是
 *
 *     setFinishValue(targetValue !== null ? String(targetValue) : '')
 *
 * 于是输入框里**预填了目标值**：孩子顺手点「完成」，等于**替他说"我做满了目标"**。
 * 而这个数字会参与算分（`calculateCheckinPoints` = `pointsPerUnit × valueDone`）
 * 并进统计 —— 一个没留意的点击就变成一条假数据。
 *
 * ## 三条规则（本文件的全部语义）
 *
 * | 任务有量化目标 | 输入框 | 空着提交 |
 * | --- | --- | --- |
 * | **有**（`targetValue` 是 > 0 的有限数） | 默认**空**，且**必填** | **拦住**，给人话提示 |
 * | **无**（`targetValue` 是 `null` / `undefined` / `0` / 负数） | 默认空，**可选** | 放行，按既有的「一次」计 |
 *
 * ⚠️ **不预填**是硬要求：本文件不提供任何"默认值"函数，调用处一律从空串开始
 *   （`finishAmountLabel()` / `finishAmountHint()` 只给文案，不给数字）。
 * ⚠️ 只做**界面校验**，不改服务端语义：接口本来就接受正数或 `null`
 *   （`valueDone: z.number().positive().max(100000).nullable()`），这里一个字段都没动。
 * ⚠️ **无目标值不设必填**：那种任务没有"该填多少"的参照，卡一个必填项毫无意义 ——
 *   既有的计分规则是 `valueDone` 为空按 `1` 个单位算（见 `@fht/shared` 的
 *   `calculateCheckinPoints`），所以留空是**合法且有明确含义**的。
 * ⚠️ 纯函数：无 React / DOM / 网络依赖，可以被用例直接喂边界值。
 */

/** 目标值够不够格当"必填"的依据：只有 > 0 的有限数才算**设了目标**。 */
export function hasAmountTarget(targetValue: number | null | undefined): boolean {
  return typeof targetValue === 'number' && Number.isFinite(targetValue) && targetValue > 0;
}

/** 完成量是不是一个能提交的值（正数）。与服务端 schema 同口径的**界面**判断。 */
export function isValidAmount(raw: string): boolean {
  const trimmed = raw.trim();
  if (trimmed === '') return false;
  const n = Number(trimmed);
  return Number.isFinite(n) && n > 0;
}

/** 校验结论：放行 → 要提交的 `valueDone`（可能是 `null` = 完成一次）；拦住 → 给孩子看的话。 */
export type FinishAmountCheck =
  | { ok: true; valueDone: number | null }
  | { ok: false; error: string };

/** 拦住空输入时给孩子看的话 —— **人话**，不说"必填字段"。 */
export const FINISH_AMOUNT_REQUIRED = '还没填你实际完成了多少呢，填好再点「完成」';
/** 填了数字但不合法（0、负数、乱敲）时的话。 */
export const FINISH_AMOUNT_INVALID = '填一个大于 0 的数字吧，比如 1、2.5';
/**
 * 有目标值时输入框旁边**常驻**的那句 —— 不能只把按钮置灰：
 * 孩子得先知道这一格要自己填，而不是点不动却不知道为什么。
 */
export const FINISH_AMOUNT_FIELD_HINT = '请填一下你实际完成了多少（不会帮你填好）';

/**
 * 判定这次「完成」能不能提交，以及要提交的 `valueDone`。
 *
 * 取值顺序（每一步都有理由，不要调换）：
 *  1. 输入不是空串 → 必须是正数；否则拦住（**与有没有目标值无关**）。
 *  2. 输入是空串 + 任务设了目标值 → **拦住**（这就是用户要的那一条：不填跳不过去）。
 *  3. 输入是空串 + 任务没设目标值 → 放行，`valueDone: null`（按「完成一次」计分）。
 */
export function checkFinishAmount(
  raw: string,
  targetValue: number | null | undefined,
): FinishAmountCheck {
  const trimmed = raw.trim();

  if (trimmed !== '') {
    if (!isValidAmount(trimmed)) return { ok: false, error: FINISH_AMOUNT_INVALID };
    return { ok: true, valueDone: Number(trimmed) };
  }

  if (hasAmountTarget(targetValue)) return { ok: false, error: FINISH_AMOUNT_REQUIRED };

  return { ok: true, valueDone: null };
}

/** 数字转成给人看的字符串：拿不到就「—」，**不吞成 0**。 */
export function formatAmountText(value: number | null | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';
}

/** 输入框的标签：有目标值就把目标写在标签里，孩子一眼知道在跟什么比。 */
export function finishAmountLabel(
  targetValue: number | null | undefined,
  unit: string | null | undefined,
): string {
  if (!hasAmountTarget(targetValue)) return '这次完成了多少（可选）';
  const target = formatAmountText(targetValue);
  return `这次完成了多少？${unit ? `（目标 ${target}${unit}）` : `（目标 ${target}）`}`;
}

/** 输入框下面的说明：有目标值必须填（措辞与常驻提示**同一句**，避免两处漂移），没目标值留空即可。 */
export function finishAmountHint(targetValue: number | null | undefined): string {
  return hasAmountTarget(targetValue)
    ? FINISH_AMOUNT_FIELD_HINT
    : '没有量化目标，留空就按「完成一次」算';
}
